import { captureException } from '@sentry/react';
import amplitude from 'amplitude-js';
import { useCallback, useEffect, useRef } from 'react';

import { DatePreset } from 'create/molecules/filters/date-range-filter/DateRangeFilter';
import { TimeZone } from 'create/molecules/time-zone-switch/TimeZoneSwitch';
import { Metric } from 'create/organisms/insights-overview/InsightsOverview';
import { WizardStep } from 'create/organisms/wizard-create-track/wizardCreateTrackTypes';
import { CheckType } from 'play/molecules/checks-creation/list-of-existing-checks/ListOfExistingChecks';
import { AccessSetting } from 'support/accessSetting';
import { ChallengeStatus, ChallengeTabType, ChallengeType, PlayType, TrackInviteType } from 'types/graphql';
import { Direction } from 'types/graphql';

import { AmplitudeMetadata, useAmplitudeMetadata } from './AmplitudeMetadataContext';

interface Events {
  'Lab - Notes - Navigated to slide': {
    slideNumber: number;
    slideCount: number;
    slidesCompletedPct: number;
    viaPagination: boolean;
  };
  'Lab - Logo clicked': { from: 'Notes' | 'Assignment' | 'Splash screen' };
  'Lab - Clicked Exit': { from: 'Notes' | 'Assignment' | 'Expired screen' };
  'Lab - Challenge - Started': { challengeNumber: number };
  'Lab - Challenge - Checked': { type: ChallengeType; status: ChallengeStatus; attempts: number };
  'Lab - Challenge - Clicked environment loading timer': never;
  'Lab - Challenge - Opened notes': never;
  'Lab - Challenge - Closed notes': never;
  'Lab - Challenge - Copied to clipboard': never;
  'Lab - Challenge - Clicked correct panel': never;
  'Lab - Challenge - External window button shown': never;
  'Lab - Challenge - Opened external window': { action: 'opened' | 'focused' };
  'Lab - Challenge - Toggled accordion': {
    action: 'expand' | 'collapse';
    accordionNumber: number;
    accordionCount: number;
    accordionNumberPct: number;
  };
  'Lab - Challenge - Toggled sidebar': { action: 'opened' | 'closed' };
  'Lab - Challenge - Skipped': { type: ChallengeType };
  'Lab - Challenge - Refreshed tab': { type: ChallengeTabType };
  'Lab - Challenge - Error Challenge is locked': never;
  'Lab - Challenge - UI Check Added': { type: CheckType };
  'Lab - Clicked custom finish track button': { text: string };
  'Lab - Clicked finish track button': never;
  'Lab - Enter fullscreen': { pageType: string };
  'Lab - Exit fullscreen': { pageType: string };
  'Lab - Feedback - Rated track': { rating: number };
  'Lab - Feedback - Started writing': never;
  'Lab - Feedback - Sent': never;
  'Lab - Session expired': never;
  'Lab - Track - Starting - Switched focus': { action: 'focus' | 'blur' };
  'Lab - Splash - Landed': never;
  'Lab - Splash - Launched track': never;
  'Lab - Recap - Landed': { disabledFeedback: boolean; customFinishText: boolean; customFinishButton: boolean };
  'Lab - Track - Starting': { estimatedTime: number };
  'Lab - Track - Starting - Notification permission requested': never;
  'Lab - Track - Starting - Notification permission granted': never;
  'Lab - Track - Starting - Notification sent': never;
  'Lab - Track - Starting - Notification clicked': never;
  'Lab - Track - Ready to start': { hasFocus: boolean };
  'Lab - Track - Started': { showShareButton: boolean };
  'Lab - Track - Failed to start': { reason: string };
  'Lab - Track - Completed': never;
  'Lab - Clicked Track Outline': never;
  'Lab - Clicked Skip to': never;
  'Lab - Confirmed Skip': never;
  'Lab - Clicked Restart Track': never;
  'Lab - Confirmed Restart Track': never;
  'Lab - Idle - Popup displayed': never;
  'Lab - Idle - User still there': never;
  'Lab - Branding Modal - View website': never;
  'Lab - Clicked Share': {
    from: 'Notes screen' | 'Finish screen';
    invite_access_setting: AccessSetting;
  };
  'Track Invite overview - Landed': { showShareButton: boolean };
  'Track Invite overview - Clicked Share': never;
  'Track Invite overview - Track card - Clicked Card': never;
  'Track Invite overview - Track card - Clicked View details': never;
  'Track Invite overview - Track card - Clicked Start': never;
  'Track Invite overview - Track card - Clicked Continue': never;
  'Track Invite overview - Track card - Clicked Stop': never;
  'Track Invites - User activity - Toggled activity log': { action: 'opened' | 'closed' };
  'Track Invites - Clicked Create': never;
  'Track Invites - Copied link': {
    from: 'Track Invite detail' | 'Track Invites context menu' | 'Live dashboard header';
    type: TrackInviteType;
  };
  'Track Invites - Clicked Edit': { from: 'Track Invite detail' | 'Track Invites context menu' };
  'Track Invites - Clicked Duplicate': { from: 'Track Invites context menu' };
  'Track Invites - Activity report - Clicked View in Users report': never;
  'Track Invites - Clicked View activity report': {
    from: 'Track Invite detail' | 'Track Invites context menu' | 'Track Invites Claims column';
    type: TrackInviteType;
  };
  'Track Invites - Clicked Download CSV Report': {
    from: 'Track Invites context menu' | 'User activity' | 'Form submissions';
    type: TrackInviteType;
  };
  'Track Invites - Edit Track Invite - Clicked Save': { type: TrackInviteType; access_setting: AccessSetting };
  'Track Invites - Edit Track Invite - Successfully edited': { type: TrackInviteType; access_setting: AccessSetting };
  'Track Invites - Duplicate Track Invite - Clicked Save': { type: TrackInviteType; access_setting: AccessSetting };
  'Track Invites - Duplicate Track Invite - Successfully duplicated': {
    type: TrackInviteType;
    access_setting: AccessSetting;
  };
  'Track Invites - New Track Invite - Clicked Save': { type: TrackInviteType; access_setting: AccessSetting };
  'Track Invites - New Track Invite - Successfully created': { type: TrackInviteType; access_setting: AccessSetting };
  'Tracks - Embed Configuration - Added custom finish track button': never;
  'Tracks - Embed Configuration - Customised finish text': never;
  'Tracks - Embed Configuration - Disabled feedback': never;
  'Tracks - New Authoring Toggle': { enabled: boolean };
  'Tracks - New Track - Clicked Create': never;
  'Tracks - New Track - Clicked Create from scratch': never;
  'Tracks - New Track - Wizard - Clicked Next': {
    from: WizardStep;
    to: WizardStep;
  };
  'Tracks - New Track - Wizard - Clicked Back': {
    from: WizardStep;
    to: WizardStep;
  };
  'Tracks - New Track - Wizard - Clicked Finish': never;
  'Live Events - Hot Start Banner - Dismissed': never;
  'Live Events - Inspected': { from: 'Attendees Overview context menu' | 'Sandboxes context menu' | 'Activity log' };
  'Live Events - Viewed activity log': {
    from: 'Attendees Overview context menu' | 'Attendees Overview name' | 'Activity tab switcher';
  };
  'Live Events - Viewed sandboxes': { from: 'Attendees Overview context menu' | 'Activity tab switcher' };
  'Live Events - Remove attendee': { from: 'Attendees Overview context menu' };
  'Live Events - Sandboxes - Stopped sandbox': never;
  'Live Events - Sandboxes - Extended TTL': never;
  'Live Events - Go to Live dashboard': { from: 'Track Invite detail' | 'Track Invites context menu' };
  'Hot Start - Clicked Create': {
    from: 'Live Events dashboard banner' | 'Invite context menu' | 'Manage Hot Start';
  };
  'Hot Start - Successfully created': never;
  'Hot Start - Clicked Duplicate': {
    type: 'Scheduled' | 'Always hot';
    from: 'Hot Start detail' | 'Hot Start context menu';
  };
  'Track Invite overview - Team banner - Clicked Study Room link': never;
  'Track Invite overview - Team banner - Clicked Powered by Instruqt': never;
  'Track Invite overview - Landing triggered form': {
    formFieldsCount: number;
  };
  'Track Invite overview - Form submitted successfully': {
    formFieldsCount: number;
  };
  'Track Invite overview - Form submitted unsuccessfully': {
    formFieldsCount: number;
  };
  'Track Invite overview - Content accessed (Invite claimed)': never;
  'Track Invites - Form submissions - Viewed': never;
  'Track Invites - Activity report - Apply date filter': {
    from: 'User activity' | 'Form submissions';
    range: 'Last 7 days' | 'Last 30 days' | 'Last 3 months' | 'Last year' | 'All time';
  };
  'Track Invites - Activity report - Apply search filter': {
    from: 'User activity' | 'Form submissions';
  };
  'Track - Website Service Added': {
    teamSlug: string;
  };
  'Track - Challenge - Virtual Browser Tab Added': never;
  'Track Invites - Form Tab - Updated': {
    fields: string[];
  };
  'Track Invites - Form Tab - Navigated': {
    referral: 'track-invite-link' | 'organic';
  };
  'Track Invite overview - Form skipped (already submitted before)': never;
  'Plays report - Landed': never;
  'Plays report - Open drawer': never;
  'Plays report - Pagination': {
    page: number;
    pageSize: number;
  };
  'Plays report - Play drawer - Click see user activity': never;
  'Plays report - Play drawer - Click invite link': never;
  'Plays report - Play drawer - Click tab': {
    tab: 'overview' | 'activity' | 'user';
  };
  'Plays report - Filters - Applied date filter': {
    preset: DatePreset;
    customDuration?: number;
  };
  'Plays report - Filters - Applied track filter': {
    tracksSelected: number;
  };
  'Plays report - Filters - Applied custom parameters filter': {
    key: string;
  };
  'Plays report - Filters - Applied invite filter': {
    invitesSelected: number;
  };
  'Plays report - Filters - Cleared': { filter: 'track filter' | 'invite filter' | 'custom parameter' };
  'Plays report - Filters - Clear all': never;
  'Plays report - Download CSV': {
    dateFilterValue: string;
    trackFilterApplied: boolean;
    customParameterFilterApplied: boolean;
    sortedBy: string;
  };
  'Plays report - Sorting changed': {
    sortyBy: string;
    sorting: Direction;
  };
  'User Profile dropdown - Clicked Financial dashboard': never;
  'Insights - Filters - Clicked Filter options': never;
  'Insights - Filters - Applied filters': never;
  'Insights - Clicked Financial dashboard': never;
  'Users report - Landed': never;
  'Users report - Open drawer': never;
  'Users report - Pagination': {
    page: number;
    pageSize: number;
  };
  'Users report - Sorting changed': {
    sortyBy: string;
    sorting: Direction;
  };
  'Users report - Filters - Applied date filter': {
    preset: DatePreset;
    customDuration?: number;
  };
  'Users report - Filters - Applied track filter': {
    tracksSelected: number;
  };
  'Users report - Filters - Applied invite filter': {
    invitesSelected: number;
  };
  'Users report - Filters - Applied user filter': {
    usersSelected: number;
  };
  'Users report - Filters - Cleared': {
    filter: 'track filter' | 'invite filter' | 'user filter';
  };
  'Users report - Filters - Clear all': never;
  'Users report - Download CSV': {
    dateFilterValue: string;
    trackFilterApplied: boolean;
    userFilterApplied: boolean;
    inviteFilterApplied: boolean;
    sortedBy: string;
  };
  'Users report - User drawer - Click see all user activity': never;
  'Users report - User drawer - Click play card': never;
  'Insights - Landed': never;
  'Insights - Table - Pagination': {
    page: number;
    pageSize: number;
  };
  'Insights - Filters - Applied date filter': {
    preset: DatePreset;
    customDuration?: number;
  };
  'Insights - Filters - Applied track filter': {
    tracksSelected: number;
  };
  'Insights - Filters - Applied track invites filter': {
    invitesSelected: number;
  };
  'Insights - Filters - Applied track tags filter': {
    tagsSelected: number;
  };
  'Insights - Filters - Applied authors filter': {
    authorsSelected: number;
  };
  'Insights - Filters - Applied developer plays filter': {
    playType: PlayType;
  };
  'Insights - Metrics - Switched': {
    metric: Metric;
  };
  'Insights - Switched time zone': {
    timeZoneSelected: TimeZone;
  };
  'Insights - Filters - Cleared': {
    filter:
      | 'track filter'
      | 'track tags filter'
      | 'authors filters'
      | 'developer plays filter'
      | 'invite filter'
      | 'developer plays filter'
      | 'time zone';
  };
  'Insights - Filters - Clear all': never;
  'Insights - Download CSV - Insights overview': {
    dateFilterValue: string;
    trackFilterApplied: boolean;
    trackInvitesFilterApplied: boolean;
    trackTagsFilterApplied: boolean;
    authorFilterApplied: boolean;
    developerPlaysFilterApplied: boolean;
  };
  'Insights - Download CSV - popular content table': {
    dateFilterValue: string;
    trackFilterApplied: boolean;
    trackInvitesFilterApplied: boolean;
    trackTagsFilterApplied: boolean;
    authorFilterApplied: boolean;
    developerPlaysFilterApplied: boolean;
  };
}

type Event<TName extends keyof Events> = Events[TName] extends never
  ? { name: TName }
  : { name: TName; data: Events[TName] };

export function trackAmplitudeEvent<T extends keyof Events>(event: Event<T>, metadata: AmplitudeMetadata): void {
  try {
    const data = { ...metadata };

    if ('data' in event) {
      Object.assign(data, event.data);
    }

    amplitude.getInstance().logEvent(event.name, data);
  } catch (e) {
    captureException(e);
  }
}

export function useTrackAmplitudeEvent() {
  const metadata = useAmplitudeMetadata();
  const ref = useRef(metadata);

  useEffect(() => {
    ref.current = metadata;
  }, [metadata]);

  return useCallback(<T extends keyof Events>(event: Event<T>) => {
    trackAmplitudeEvent(event, ref.current);
  }, []);
}
